import type { FetchError } from "@selector/shared";
import { TrackEnum, TrackTextEnum } from "@selector/shared/openapi";
import { produce } from "immer";
import type { StateCreator } from "zustand";

import { setupWorkerMessageHandler } from "../../main-canvas/messages/handle-track-messages.ts";
import type { RenderingOptions } from "../../types.ts";
import { type SubType, type TrackConfiguration, type TrackGroups, type Tracks, type TrackType } from "../../types.ts";
import { getColorByTrackType } from "../../utils/graphics/color-conversion.ts";
import { createNewTracks, type OtherTrackConfig, type TrackConfig } from "../../utils/tracks/create-tracks.ts";
import type { GenomeViewerState, GenomeViewerStore } from "../store.ts";

export interface TracksState {
  alignedTrackGroupStarts: Map<string, number> | undefined;
}

export interface TracksActions {
  // Track CRUD operations (moved from main-slice)
  /**
   * Add a new track in an existing group or a brand new group.
   * It will send the "render" message to the corresponding worker
   */
  createTracks(
    tracksToCreate: TrackConfig[],
    onError: (error: Error | FetchError) => void,
    afterGroupOrTrackId?: string,
  ): Set<{ trackId: string; groupId: string }>;
  updateTrack: (trackId: string, data: Partial<RenderingOptions>) => void;
  removeTracks(trackDeletions: { trackId: string; groupId: string }[], removeEmptyGroups?: boolean): void;
  setTrackGroupVisible: (trackGroupId: string, visible: boolean) => void;
  updateTrackContigId: (trackGroupId: string, contigId?: number, toBase?: number) => void;
  removeTrackGroups: (trackGroupIds: string[]) => void;

  // Track configuration management
  getTrackConfiguration: () => TrackConfiguration;
  applyTrackConfiguration: (
    currentTrackConfiguration: TrackConfiguration,
    newTrackConfiguration: TrackConfiguration,
    onError: (error: Error | FetchError) => void,
  ) => void;
  changeTrackGroupPosition: (trackGroups: TrackGroups) => void;
  reorderTrackGroupsById: (orderedTrackGroupIds: string[]) => void;
  reorderTracksInGroup: (groupId: string, trackIds: string[]) => void;
}

export type TracksSlice = TracksState & TracksActions;

export const initialTracksState: TracksState = {
  alignedTrackGroupStarts: undefined,
};

function removeTracksUtil<T extends Tracks, G extends TrackGroups, F extends { members: { trackId: string }[] }>(
  trackDeletions: { trackId: string; groupId: string }[] = [],
  { tracks, trackGroups, alignedFamily }: { tracks: T; trackGroups: G; alignedFamily?: F },
  removeEmptyGroup = false,
  busyList: Set<string>,
  waitingList: Set<string>,
): { tracks: T; trackGroups: G; alignedFamily?: F } {
  for (const { trackId, groupId } of trackDeletions) {
    const track = tracks.get(trackId);
    const trackGroup = trackGroups.get(groupId);

    if (!trackGroup || !track) {
      continue;
    }

    const trackIds = trackGroup?.trackIds ?? [];
    track.worker.terminate();
    tracks.delete(trackId);
    busyList.delete(trackId);
    waitingList.delete(trackId);

    const newTrackIds = trackIds.filter((id) => id !== trackId);

    if (newTrackIds.length === 0 && removeEmptyGroup) {
      trackGroups.delete(groupId);
    } else {
      trackGroups.set(groupId, { ...trackGroup, trackIds: newTrackIds });
    }

    if (alignedFamily) {
      // remove members of deleted tracks
      alignedFamily.members = alignedFamily.members.filter((member) => member.trackId !== trackId);
    }
  }

  // Do not return alignedFamily if any to not overwrite it in the update
  if (alignedFamily) {
    return { trackGroups, tracks, alignedFamily };
  }

  return { trackGroups, tracks };
}

export const createTracksSlice: StateCreator<GenomeViewerStore, [], [], TracksSlice> = (set, get) => ({
  ...initialTracksState,

  createTracks(tracksToCreate = [], onError, afterGroupOrTrackId) {
    const { tracks, trackGroups, organisms } = get();

    const { newTrackSet, ...storeUpdate } = createNewTracks(
      tracksToCreate,
      {
        organisms,
        trackGroups,
        tracks,
      },
      (w) => setupWorkerMessageHandler(w, set, get, onError),
      afterGroupOrTrackId,
    );

    set(storeUpdate);
    return newTrackSet;
  },

  updateTrack(trackId, data) {
    set(
      produce((state: GenomeViewerState) => {
        const track = state.tracks.get(trackId);
        if (track) {
          Object.assign(track, data);
        }
      }),
    );
    get().refreshTracks();
  },

  removeTracks(trackDeletions = [], removeEmptyGroups = false) {
    const { tracks, trackGroups, alignedFamily, busyList, waitingList } = get();
    const update = removeTracksUtil(
      trackDeletions,
      { trackGroups, tracks, alignedFamily },
      removeEmptyGroups,
      busyList,
      waitingList,
    );

    set(update);
    get().refreshTracks();
  },

  removeTrackGroups(trackGroupIds) {
    const { trackGroups, removeTracks } = get();
    const trackDeletions = [];

    for (const groupId of trackGroupIds) {
      const trackIds = trackGroups.get(groupId)?.trackIds ?? [];

      // Terminate worker and all tracks from the group
      for (const trackId of trackIds) {
        trackDeletions.push({ trackId, groupId });
      }
    }
    if (trackDeletions.length) {
      removeTracks(trackDeletions, true);
    }
    get().refreshTracks();
  },

  setTrackGroupVisible(trackGroupId, visible) {
    set(
      produce((state: GenomeViewerState) => {
        const group = state.trackGroups.get(trackGroupId);
        if (group) {
          group.graphics.isVisible = visible;
        }
      }),
    );
    get().refreshTracks();
  },

  updateTrackContigId(trackGroupId, contigId, toBase) {
    const { alignedFamily, trackGroups, organisms, tracks } = get();
    const trackGroup = trackGroups.get(trackGroupId);

    if (!trackGroup) return;

    const organism = organisms.get(trackGroup.organismId);

    if (!organism) return;

    const contig = contigId ? organism.contigs.get(contigId) : organism.contigs.values().next().value;

    if (!contig) return;

    trackGroup.graphics.length = contig.length;

    trackGroups.set(trackGroupId, {
      ...trackGroup,
      contigId: contig.id,
      contigName: contig.name,
      graphics: {
        ...trackGroup.graphics,
        baseShift: toBase ?? trackGroup.graphics.baseShift,
      },
    });

    for (const trackId of trackGroup.trackIds) {
      tracks.set(trackId, {
        ...tracks.get(trackId)!,
        isLoaded: false,
      });
    }

    if (alignedFamily) {
      // remove members of deleted tracks
      alignedFamily.members = alignedFamily.members.filter((member) => !trackGroup.trackIds.includes(member.trackId));
    }

    set({
      alignedFamily,
      trackGroups: new Map(trackGroups),
      tracks,
      needsRefresh: true,
    });
  },

  getTrackConfiguration() {
    const trackConfiguration: TrackConfiguration = new Map();

    // Map in the trackGroups
    for (const [groupId, trackGroup] of get().trackGroups) {
      const defaultTrackTypes: TrackType[] = [];
      const nuFeatureTypes: SubType[] = [];

      // Get the list of all track types within this trackGroup
      for (const trackId of trackGroup.trackIds) {
        const track = get().tracks.get(trackId);
        if (track) {
          if (track.trackType === TrackEnum.NUCLEOTIDE_FEATURE && track.subType) {
            nuFeatureTypes.push(track.subType);
          } else {
            defaultTrackTypes.push(track.trackType);
          }
        }
      }
      trackConfiguration.set(groupId, {
        defaultTypes: defaultTrackTypes,
        nuFeatureTypes: nuFeatureTypes,
      });
    }
    return trackConfiguration;
  },

  applyTrackConfiguration(currentTrackConfiguration, newTrackConfiguration, onError) {
    const { tracks: originalTracks, trackGroups: originalTrackGroups, organisms, alignedFamily, syntenyWorker } = get();

    const trackGroups = new Map(originalTrackGroups);
    const tracks = new Map(originalTracks);
    const newTrackConfigs: TrackConfig[] = [];
    const trackDeletions = [];

    // Map in the new track configuration
    for (const [groupId, trackFeatures] of newTrackConfiguration) {
      // Compute the added/removed tracks
      const added = trackFeatures.defaultTypes.filter(
        (trackType) => !currentTrackConfiguration.get(groupId)?.defaultTypes.includes(trackType),
      );
      const removed = currentTrackConfiguration
        .get(groupId)!
        .defaultTypes.filter((trackType) => !trackFeatures.defaultTypes.includes(trackType));

      // Compute the added/removed tracks of nuFeatures
      const addedNuFeatureTypes = trackFeatures.nuFeatureTypes.filter(
        (featureType) =>
          !currentTrackConfiguration.get(groupId)?.nuFeatureTypes.some((type) => type.id === featureType.id),
      );

      const removedNuFeatureTypes = currentTrackConfiguration
        .get(groupId)!
        .nuFeatureTypes.filter(
          (featureType) => !trackFeatures.nuFeatureTypes.some((type) => type.id === featureType.id),
        );

      // Load the added tracks
      for (const trackType of added) {
        newTrackConfigs.push({ trackType, group: groupId } as OtherTrackConfig);
      }

      // Load the added tracks of nuFeatures
      for (const featureType of addedNuFeatureTypes) {
        newTrackConfigs.push({
          trackType: TrackEnum.NUCLEOTIDE_FEATURE,
          group: groupId,
          options: {
            subType: featureType,
            color: getColorByTrackType(TrackEnum.NUCLEOTIDE_FEATURE),
            text: TrackTextEnum.INNER,
            isVisible: true,
          },
        });
      }

      // "Unmount" and clean the removed tracks
      for (const trackType of removed) {
        const track = [...tracks.values()].find((track) => track.trackType === trackType && track.groupId === groupId);
        if (track) {
          trackDeletions.push({ trackId: track.trackId, groupId: track.groupId });
        }
      }

      // "Unmount" and clean the removed tracks of nuFeatures
      for (const featureType of removedNuFeatureTypes) {
        const track = [...tracks.values()].find(
          (track) =>
            track.trackType === TrackEnum.NUCLEOTIDE_FEATURE &&
            track.subType?.id === featureType.id &&
            track.groupId === groupId,
        );
        if (track) {
          trackDeletions.push({ trackId: track.trackId, groupId: track.groupId });
        }
      }
    }

    // Use removeTracks utility function directly to avoid circular dependencies
    const { busyList, waitingList } = get();
    const removeUpdate = removeTracksUtil(
      trackDeletions,
      { trackGroups, tracks, alignedFamily },
      false,
      busyList,
      waitingList,
    );
    const addUpdate = createNewTracks(
      newTrackConfigs,
      {
        organisms,
        trackGroups: removeUpdate.trackGroups,
        tracks: removeUpdate.tracks,
      },
      (w) => setupWorkerMessageHandler(w, set, get, onError),
    );

    set(addUpdate);
    // sendSyntenyRenderMessage();
    get().sendSyntenyRenderMessage(syntenyWorker);
    // no need to render as already done when pressing apply in the track config dialog
    get().refreshTracks();
  },

  changeTrackGroupPosition(trackGroups: TrackGroups) {
    set({ trackGroups });
    get().refreshMain();
    get().sendSyntenyRenderMessage(get().syntenyWorker);
  },

  reorderTrackGroupsById(orderedTrackGroupIds: string[]) {
    const trackGroupsLookup = get().trackGroups;

    const trackGroups: TrackGroups = new Map(
      orderedTrackGroupIds
        .filter((id) => trackGroupsLookup.has(id))
        .map((id) => {
          const trackGroup = trackGroupsLookup.get(id)!;
          return [id, trackGroup];
        }),
    );

    set({ trackGroups });
  },

  reorderTracksInGroup: (groupId, trackIds) => {
    const trackGroups = get().trackGroups;
    const trackGroup = trackGroups.get(groupId);
    const syntenyWorker = get().syntenyWorker;

    if (trackGroup) {
      trackGroup.trackIds = trackIds;
    }

    set({
      trackGroups: new Map(trackGroups),
    });

    get().refreshMain();
    get().sendSyntenyRenderMessage(syntenyWorker);
  },
});
